// KeepViewCurrentPanel.js
// "Keep this View current" — the rail's summary of the automations a view owns,
// and the door to the dialog that configures them.
//
// WHAT IT DOES: a view refreshes itself through one automation per CARD (widget
// instance) — created, configured and removed in KeepViewCurrentDialog, owned
// by this view, Email off, every one on the view's own daily 06:00 schedule
// (utils/viewAutomations.js VIEW_SCHEDULE). Each runs with the filters
// configured for its card (dynamic dates allowed), and every run it starts is
// stamped with the card, so the card adopts the result and shows its
// parameters (utils/viewCards.js). Anyone who needs numbers fresher than the
// morning presses Run.
//
// OWNERSHIP IS ABSOLUTE: this panel reads and writes ONLY triggers marked
// `managed_by: {type: 'view', view_id}`. A trigger a person created — however
// similar its schedule — is invisible here: never counted as coverage, never
// enabled or disabled, and never stripped of its Email permission by
// commitTriggerConfig's config-change invariant (H-15). The price is honest
// duplication: a function someone already schedules themselves will also run
// for the view's own automation.
//
// Orphan cleanup verifies departure against the current saved View. Opening
// a View never adopts or creates an automation; card IDs are deterministic.
//
// The dialog closes itself when the View changes underneath its drafts (a card
// leaves or arrives, a function is republished, the View is saved elsewhere);
// this panel then says "This view changed." until that is dismissed or the
// dialog is opened again.
//
// A failed read of the view's automations blocks everything: with the list
// unknown, configuring would create a second set beside the real one (M103).
// The panel shows the failure with Retry and the dialog stays shut until a
// read succeeds. Publishing a new version of a function keeps its view
// automations, carrying their filters forward, and switches one off with a
// reason only when a filter no longer applies (§5.11.4) — such a card reads
// "Off" here until it is reviewed in the dialog.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LoadFailure from '../common/LoadFailure';
import { formatText } from '../common/TextFormatterProvider';
import {
  describeWhen,
  watchViewTriggers,
  saveViewAutomations,
} from '../../services/functionTriggers';
import { planReconcile, triggerForCard } from '../../utils/viewAutomations';
import KeepViewCurrentDialog from './KeepViewCurrentDialog';

function KeepViewCurrentPanel({
  companyName,
  viewId,
  // Names the automations the dialog creates.
  viewName,
  // The cards this view can automate, in card order: each with its function
  // (published version and data-source blocks) and its rail's current request
  // parameters. Memoised by the caller.
  cards,
  // EVERY card instance on the view, including those `cards` leaves out
  // because their function is unpublished or invalid: an automation is
  // "departed" only when its card is gone from here.
  instanceIds = null,
  // Whether `cards` reflects the current view document. A view starts loading
  // with no cards resolved, which must never read as "every card has left".
  ready = true,
  // The tables the cards' functions read, for the dialog's watched-table
  // picker and attribute detection.
  tables = [],
  actorUid,
  onAutomationChange,
  viewChanged = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const [ownTriggers, setOwnTriggers] = useState([]);
  // Whether ownTriggers is the list Firestore holds. A failed read leaves it
  // false — and the dialog shut — until Retry succeeds.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  // The dialog closed itself because the View changed underneath its drafts.
  const [viewChangedNotice, setViewChangedNotice] = useState(false);
  const handleOpenDialog = () => {
    setViewChangedNotice(false);
    setDialogOpen(true);
  };
  const handleViewChangedWhileOpen = useCallback(() => {
    setDialogOpen(false);
    setViewChangedNotice(true);
  }, []);
  const parameterError = (cards || []).find((card) => card.parameterError)?.parameterError || '';

  // One single-field query: `where('managed_by.view_id', '==', …)` alone
  // needs no composite index.
  useEffect(() => {
    if (!companyName || !viewId) {
      setOwnTriggers([]);
      setLoaded(false);
      setLoadError(false);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    setLoadError(false);
    const unsubscribe = watchViewTriggers(companyName, viewId, (triggers) => {
      if (cancelled) return;
      setOwnTriggers(triggers);
      setLoaded(true);
      setLoadError(false);
    }, (err) => {
      console.error('Failed to watch this View’s automations:', err);
      if (cancelled) return;
      setLoaded(false);
      setLoadError(true);
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [companyName, viewId, loadAttempt]);

  const rows = useMemo(() => (cards || []).map((card) => ({
    card,
    trigger: triggerForCard(viewId, card.instanceId, ownTriggers),
  })), [cards, ownTriggers, viewId]);

  const tableNameById = useMemo(() => (tables || []).reduce((names, table) => (
    table?.id ? { ...names, [table.id]: formatText(table.name || table.id) } : names
  ), {}), [tables]);

  // An own automation is coverage only when it is switched on AND pinned to
  // the version its function publishes now: both dispatchers disable a stale
  // pin instead of firing it.
  const isCurrentFor = (trigger, func) => (
    Boolean(trigger?.enabled) && trigger.function_version === func.publishedVersion
  );
  const coveredCount = rows.filter((row) => isCurrentFor(row.trigger, row.card.func)).length;
  const hasEnabledAutomation = ownTriggers.some((trigger) => trigger.enabled);
  const emailCount = ownTriggers.filter((trigger) => trigger.allow_email === true).length;

  useEffect(() => {
    if (onAutomationChange) onAutomationChange(hasEnabledAutomation);
  }, [hasEnabledAutomation, onAutomationChange]);

  // Membership reconciliation (see the header). One attempt per distinct
  // state of (cards shown, triggers owned): a write that fails is reported
  // once and not retried on every render.
  const reconcileAttemptRef = useRef('');
  useEffect(() => {
    if (!ready || !loaded || dialogOpen || viewChanged) return;
    const plan = planReconcile({ viewId, cards, ownTriggers, instanceIds });
    if (plan.deleteIds.length === 0) {
      reconcileAttemptRef.current = '';
      return;
    }
    if (busy) return;
    const signature = JSON.stringify([
      viewId,
      [...(instanceIds || (cards || []).map((card) => card.instanceId))].sort(),
      [...plan.deleteIds].sort(),
    ]);
    if (reconcileAttemptRef.current === signature) return;
    reconcileAttemptRef.current = signature;

    const reconcile = async () => {
      setBusy(true);
      setError('');
      try {
        await saveViewAutomations(companyName, {
          viewId,
          deleteIds: plan.deleteIds,
          actorUid,
        });
      } catch (err) {
        console.error('Failed to reconcile this view\'s automations:', err);
        setError(`Failed to update this view's automations: ${err.message}`);
      } finally {
        setBusy(false);
      }
    };
    reconcile();
  });

  const viewChangedAlert = viewChangedNotice && (
    <Alert
      severity="info"
      sx={{ mt: 1 }}
      action={(
        <Button color="inherit" size="small" onClick={() => setViewChangedNotice(false)}>
          Dismiss
        </Button>
      )}
    >
      This view changed.
    </Alert>
  );
  const dialog = (
    <KeepViewCurrentDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      companyName={companyName}
      viewId={viewId}
      viewName={viewName}
      cards={cards}
      ownTriggers={ownTriggers}
      tables={tables}
      actorUid={actorUid}
      stale={viewChanged}
      onViewChanged={handleViewChangedWhileOpen}
    />
  );

  const cardCount = (cards || []).length;
  const hasCards = cardCount > 0;

  const status = !loaded
    ? (loadError ? null : 'Loading…')
    : (coveredCount === 0
      ? 'Off'
      : (coveredCount === cardCount
        ? 'Every card refreshes automatically'
        : `${coveredCount} of ${cardCount} cards refresh automatically`));

  const rowChip = (row) => {
    if (!row.trigger) return <Chip size="small" variant="outlined" label="Not set" sx={{ height: 22 }} />;
    if (isCurrentFor(row.trigger, row.card.func)) return <Chip size="small" color="success" label="On" sx={{ height: 22 }} />;
    if (row.trigger.enabled) return <Chip size="small" color="warning" label="Needs update" sx={{ height: 22 }} />;
    return (
      <Tooltip title={row.trigger.disabled_reason || 'Switched off — nothing runs until it is switched on'}>
        <Chip size="small" label="Off" sx={{ height: 22 }} />
      </Tooltip>
    );
  };

  const summary = (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          Keep current
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded((open) => !open)}
          aria-label={expanded ? 'Hide automations' : 'Show automations'}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Button
        size="small"
        variant="outlined"
        onClick={handleOpenDialog}
        disabled={!ready || !loaded || busy || Boolean(parameterError) || viewChanged}
        sx={{ mt: 0.75 }}
      >
        Configure…
      </Button>
      {parameterError && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{parameterError}</Typography>}

      {status && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`${status}. `}
          {emailCount > 0
            ? `${emailCount === 1 ? 'One of these automations still has' : `${emailCount} of these automations still have`} email switched on.`
            : 'Automatic refreshes never send email.'}
        </Typography>
      )}

      {loadError && (
        <LoadFailure
          thing="this view's automations"
          onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
          align="left"
          sx={{ p: 0, mt: 1 }}
        />
      )}

      {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
    </>
  );

  const automationRows = (
    <Collapse in={expanded && loaded} unmountOnExit>
      <Box sx={{ mt: 1.5 }}>
        {rows.map((row) => (
          <Stack
            key={row.card.instanceId}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mt: 0.5 }}
          >
            <ScheduleIcon fontSize="small" color={row.trigger ? 'secondary' : 'disabled'} />
            <Tooltip title={row.trigger ? `${describeWhen(row.trigger, tableNameById)} · ${row.card.label}` : row.card.label}>
              <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {formatText(row.card.label)}
                {row.trigger ? ` · ${describeWhen(row.trigger, tableNameById)}` : ''}
              </Typography>
            </Tooltip>
            {rowChip(row)}
          </Stack>
        ))}
      </Box>
    </Collapse>
  );

  if (!hasCards && !dialogOpen && !viewChangedNotice) return null;

  // With no card to keep current the summary and the rows are hidden. The notice
  // and the dialog keep their place in the tree either way, so a dialog that
  // was open when the last card left is still the same dialog: it closes itself
  // and leaves its notice, rather than being replaced by a fresh, open one.
  return (
    <Box sx={{ mb: hasCards || viewChangedNotice ? 2 : 0 }}>
      {hasCards && summary}
      {viewChangedAlert}
      {hasCards && automationRows}
      {dialog}
    </Box>
  );
}

export default KeepViewCurrentPanel;
